import React, { useEffect, useState } from 'react'

import cx from 'classnames'
import moment, { Moment } from 'moment'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'

import { DriftDatePicker } from '@driftt/react-dates'
import { Div } from '@driftt/tide-core'

import * as FilterActions from 'modules/filter/actions'
import * as FilterSelectors from 'modules/filter/selectors'
import { ITimePeriod } from 'modules/filter/types'
import { calculateTimePeriod } from 'modules/filter/utils'
import * as OrgSelectors from 'modules/org/selectors'

import './style.css'

interface IStateProps {
  timePeriod: ITimePeriod
  orgCreatedAt: number
}

interface IDispatchProps {
  actions: {
    updateReportTimePeriod: (timePeriod: ITimePeriod) => void
  }
}

interface IComponentProps {
  onClick?: () => void
  showQuickSelects?: boolean
  anchorDirection?: 'left' | 'right'
  earliestDate?: Moment
  description?: string
}

type Props = IStateProps & IDispatchProps & IComponentProps

const TWO_CALENDAR_YEARS_BACK = moment().startOf('year').subtract(2, 'years')

const DateRangePickerComponent = ({
  timePeriod,
  onClick,
  actions,
  orgCreatedAt,
  description,
  anchorDirection = 'left',
  showQuickSelects = true,
  earliestDate = TWO_CALENDAR_YEARS_BACK,
}: Props) => {
  const [focusedInput, setFocusedInput] = useState()
  const [startDate, setStartDate] = useState(timePeriod && timePeriod.startDate)
  const [endDate, setEndDate] = useState(timePeriod && timePeriod.endDate)

  const isOutside = (day: moment.MomentInput) => {
    const momentDay = moment(day)
    return (
      momentDay.isAfter(moment(), 'day') ||
      momentDay.isBefore(orgCreatedAt) ||
      momentDay.isBefore(earliestDate)
    )
  }

  const areDatesOutOfRange = isOutside(startDate) || isOutside(endDate)

  const handleDateChange = (params: {
    startDate: Moment
    endDate?: Moment
  }) => {
    let { startDate: nextStartDate, endDate: nextEndDate } = params

    if (!nextEndDate) {
      nextEndDate = startDate
    }

    if (nextStartDate.isAfter(nextEndDate)) {
      // User has the start date in focus and selected a date beyond the end date
      nextStartDate = nextEndDate
      nextEndDate = params.startDate
    }

    if (!nextStartDate.isSame(startDate)) {
      setStartDate(nextStartDate)
    }

    if (!nextEndDate.isSame(endDate)) {
      setEndDate(nextEndDate)
    }
  }

  const onDatePickerClose = () => {
    if (
      !startDate.isSame(timePeriod.startDate) ||
      !endDate.isSame(timePeriod.endDate)
    ) {
      actions.updateReportTimePeriod(calculateTimePeriod(startDate, endDate))
    }
  }

  useEffect(() => {
    setStartDate((state) =>
      timePeriod.startDate.isSame(state) ? state : timePeriod.startDate
    )
    setEndDate((state) =>
      timePeriod.endDate.isSame(state) ? state : timePeriod.endDate
    )
  }, [timePeriod.startDate, timePeriod.endDate])

  const renderCalendarInfo = () => {
    return <Div className="date-range-picker-calendar-info">{description}</Div>
  }

  const containerClassName = cx('date-range-picker filter-container', {
    'date-range-picker--out-of-range': areDatesOutOfRange,
  })

  return (
    <Div className={containerClassName} onClick={onClick}>
      <DriftDatePicker
        startDate={startDate}
        endDate={endDate}
        anchorDirection={anchorDirection}
        onDatesChange={handleDateChange}
        focusedInput={focusedInput}
        onFocusChange={setFocusedInput}
        isOutsideRange={isOutside}
        onClose={onDatePickerClose}
        renderCalendarInfo={description ? renderCalendarInfo : null}
        showQuickSelects={showQuickSelects}
        enableOutsideDays={false}
        hideKeyboardShortcutsPanel
        readOnly
        keepFocusOnInput
        allowSingleDateSelection
      />
    </Div>
  )
}

const mapStateToProps = (state): IStateProps => ({
  timePeriod: FilterSelectors.timePeriod(state),
  orgCreatedAt: OrgSelectors.orgCreatedAt(state),
})

const mapDispatchToProps = (dispatch): IDispatchProps => ({
  actions: bindActionCreators(
    {
      updateReportTimePeriod: FilterActions.updateReportTimePeriod,
    },
    dispatch
  ),
})

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(DateRangePickerComponent)
